// src/features/admin/DataSection.jsx
//
// ADMIN-DATA-RATIONALIZE: Admin → Data section with 4 (or 5) tabs.
//
// Health tab     — integration connection cards + summary (DataHealthTabPane)
//                  hidden behind SHOW_DATA_HEALTH flag (default: false)
// Ingestion tab  — sync/ingestion event log (IngestionHistoryPanel)
//                  system-job rows excluded server-side
// Import tab     — lease import wizard (UploadStep, CSV/XLSX/PDF)
//                  JSON-paste block retired; wizard placeholder shown instead
// Backup tab     — "Backup & restore": download JSON + restore from JSON
// Maintenance tab — clear demo data + hard-delete soft-deleted leases

import React, { useState, useEffect, useCallback } from "react";
import { T } from "../../app/tokens.js";
import { DataTabHeading } from "../../components/DataTabHeading.jsx";
import { Modal } from "../../components/Modal.jsx";
import { ObjectSubNav } from "../../components/ObjectSubNav.jsx";
import { ExportPanel } from "./ExportPanel.jsx";
import { DataHealthTabPane } from "../data-health/DataHealthView.jsx";
import { IngestionHistoryPanel } from "../data-health/IngestionHistoryPanel.jsx";
import { ImportWizard } from "./ImportWizard.jsx";
import { listImportSessions } from "../../data/importSessionsClient.js";
import { log } from "../../utils/logger.js";
import {
  getSoftDeletedLeasesHealth,
  hardDeleteSoftDeletedLeases,
} from "../../data/adminClient.js";
import { SHOW_DATA_HEALTH } from "../../config/featureFlags.js";
import { RederiveDistributedSplitCard } from "./RederiveDistributedSplitCard.jsx";
import { DataReconciliationPanel } from "./DataReconciliationPanel.jsx";
import { useDataReconciliationSummary } from "../../hooks/useDataReconciliationSummary.js";

const DATA_TABS = [
  ...(SHOW_DATA_HEALTH ? [{ key: "health", label: "Health" }] : []),
  { key: "import",      label: "Import" },
  { key: "ingestion",   label: "Ingestion history" },
  { key: "export",      label: "Backup & restore" },
  { key: "maintenance", label: "Maintenance" },
];

const ADMIN_DATA_TABS = [
  ...DATA_TABS.slice(0, -1),
  { key: "reconciliation", label: "Reconciliation" },
  DATA_TABS[DATA_TABS.length - 1],
];

function readInitialDataTab(currentRole) {
  try {
    const requested = new URLSearchParams(window.location.search).get("tab");
    const tabs = currentRole === "admin" ? ADMIN_DATA_TABS : DATA_TABS;
    if (tabs.some((tab) => tab.key === requested)) return requested;
  } catch {
    // URL state is best-effort; use the normal default.
  }
  return SHOW_DATA_HEALTH ? "health" : "import";
}

const DIVIDER = {
  border: "none",
  borderTop: `0.5px solid ${T.border}`,
  margin: "28px 0",
};

const BTN_BASE = {
  padding: "5px 12px",
  borderRadius: 6,
  fontSize: T.fsSm5,
  fontWeight: T.fwSemibold,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
};

const BTN_DANGER = {
  ...BTN_BASE,
  padding: "7px 14px",
  background: T.red,
  color: T.white,
  border: "none",
};

export function DataSection({ onOpenImportsQueue, currentRole, onViewDataset, explainer }) {
  const tabs = currentRole === "admin" ? ADMIN_DATA_TABS : DATA_TABS;
  const [activeTab, setActiveTab] = useState(() => readInitialDataTab(currentRole));
  const { summary: reconciliationSummary } = useDataReconciliationSummary(currentRole);

  const [pendingCount, setPendingCount] = useState(null);
  // INGEST-IMPORT-502-DIAGNOSE-S1: the background pending-imports count fetch
  // is decoupled from page-blocking error state. A transient failure (e.g.
  // proxy 502 during workflow contention) only degrades this one count —
  // it must never block the uploader/wizard with a full-width ErrorBanner.
  const [pendingCountFailed, setPendingCountFailed] = useState(false);

  // INGEST-HISTORY-SIMPLIFY-S1: resume a staged/in-review session from the
  // Ingestion history panel. resumeNonce forces ImportWizard to remount so
  // its internal state resets cleanly on every Resume click.
  const [resumeSessionId, setResumeSessionId] = useState(null);
  const [resumeNonce, setResumeNonce] = useState(0);

  const handleResumeSession = useCallback((sessionId) => {
    setResumeSessionId(sessionId);
    setResumeNonce((n) => n + 1);
    setActiveTab("import");
  }, []);

  // Soft-deleted leases state (Maintenance tab, lazy-loaded on first open)
  const [softDeletedLeases, setSoftDeletedLeases] = useState(null); // { count, ids } | null
  const [softDeletedLoaded, setSoftDeletedLoaded] = useState(false);
  const [hardDeleteModalOpen, setHardDeleteModalOpen] = useState(false);
  const [hardDeleteBusy, setHardDeleteBusy] = useState(false);
  const [hardDeleteResult, setHardDeleteResult] = useState(null);
  const [hardDeleteError, setHardDeleteError] = useState(null);

  useEffect(() => {
    if (!tabs.some((tab) => tab.key === activeTab)) setActiveTab("import");
  }, [activeTab, tabs]);

  useEffect(() => {
    let cancelled = false;
    listImportSessions()
      .then((sessions) => {
        if (cancelled) return;
        const n = sessions.filter(
          (s) => s.status === "staged" || s.status === "in_review"
        ).length;
        setPendingCount(n);
        setPendingCountFailed(false);
      })
      .catch((err) => {
        if (cancelled) return;
        log.warn("DataSection: pending-imports count fetch failed", { err: String(err) });
        setPendingCountFailed(true);
      });
    return () => { cancelled = true; };
  }, []);

  // Lazy-load soft-deleted lease info when Maintenance tab is first opened.
  useEffect(() => {
    if (activeTab !== "maintenance" || softDeletedLoaded) return;
    let cancelled = false;
    getSoftDeletedLeasesHealth()
      .then((result) => {
        if (cancelled) return;
        setSoftDeletedLeases(result.warning || null);
        setSoftDeletedLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setSoftDeletedLoaded(true);
      });
    return () => { cancelled = true; };
  }, [activeTab, softDeletedLoaded]);

  const handleOpenHardDeleteModal = useCallback(() => {
    setHardDeleteError(null);
    setHardDeleteModalOpen(true);
  }, []);

  const handleCloseHardDeleteModal = useCallback(() => {
    if (hardDeleteBusy) return;
    setHardDeleteModalOpen(false);
    setHardDeleteError(null);
  }, [hardDeleteBusy]);

  const handleConfirmHardDelete = useCallback(async () => {
    setHardDeleteBusy(true);
    setHardDeleteError(null);
    try {
      const result = await hardDeleteSoftDeletedLeases();
      setHardDeleteModalOpen(false);
      setHardDeleteResult(result.deleted);
      setSoftDeletedLeases(null);
    } catch (err) {
      setHardDeleteError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setHardDeleteBusy(false);
    }
  }, []);

  return (
    <div data-testid="data-section">
      <h2 style={{ margin: "0 0 10px", color: T.dark, fontSize: T.fsLg5 }}>Data</h2>
      {explainer}
      <ObjectSubNav
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
        testIdPrefix="data"
        badgeTab="ingestion"
        badgeCount={pendingCount || 0}
        badges={{ reconciliation: reconciliationSummary?.activeCount ?? 0 }}
      />

      {/* ── Health tab (feature-flagged) ──────────────────────────────────── */}
      {SHOW_DATA_HEALTH && activeTab === "health" && (
        <div data-testid="data-tab-health">
          <DataHealthTabPane />
        </div>
      )}

      {/* ── Ingestion history tab ─────────────────────────────────────────── */}
      {activeTab === "ingestion" && (
        <div data-testid="data-tab-ingestion">
          <IngestionHistoryPanel
            isAdmin={currentRole === "admin"}
            onResumeSession={handleResumeSession}
          />
        </div>
      )}

      {currentRole === "admin" && activeTab === "reconciliation" && (
        <div data-testid="data-tab-reconciliation">
          <DataReconciliationPanel />
        </div>
      )}

      {/* ── Import tab ────────────────────────────────────────────────────── */}
      {activeTab === "import" && (
        <div data-testid="data-tab-import">
          <div data-testid="import-lease-data-section">
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
              <DataTabHeading
                description="Upload CSV or PDF files - leases, headcount, badging, and more. Files are staged for review before anything changes."
              >
                Import workplace data
              </DataTabHeading>
              {onOpenImportsQueue && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                  <button
                    type="button"
                    data-testid="pending-imports-btn"
                    onClick={() => onOpenImportsQueue()}
                    style={{
                      ...BTN_BASE,
                      flexShrink: 0,
                      marginTop: 4,
                      background: pendingCount ? T.blueDeep : T.card,
                      color: pendingCount ? T.white : T.mid,
                      border: pendingCount ? "none" : `1.5px solid ${T.border}`,
                      boxShadow: pendingCount ? `0 1px 4px rgba(0,0,0,0.12)` : "none",
                    }}
                  >
                    <span style={{ fontSize: T.fsXs }}>↻</span>
                    Pending imports
                    {pendingCount !== null && pendingCount > 0 && !pendingCountFailed && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: 18,
                          height: 18,
                          padding: "0 5px",
                          borderRadius: 9,
                          fontSize: T.fsXxs,
                          fontWeight: T.fwBold,
                          background: "rgba(255,255,255,0.25)",
                          color: T.white,
                        }}
                      >
                        <span data-testid="pending-imports-count">{pendingCount}</span>
                      </span>
                    )}
                    {pendingCountFailed && " (—)"}
                  </button>
                  {/* INGEST-IMPORT-502-DIAGNOSE-S1: non-blocking degraded-state
                      note. A background count-fetch failure (e.g. transient
                      502 during workflow contention) never blocks the
                      uploader/wizard with a full-width banner — it only
                      surfaces here, next to the affected count. */}
                  {pendingCountFailed && (
                    <span
                      data-testid="pending-imports-error-note"
                      role="status"
                      style={{ fontSize: T.fsXs, color: T.mid }}
                    >
                      Couldn't load pending imports
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <hr style={DIVIDER} />

          <ImportWizard
            key={resumeNonce}
            currentRole={currentRole}
            onOpenImportsQueue={onOpenImportsQueue}
            onViewDataset={onViewDataset}
            resumeSessionId={resumeSessionId}
          />
        </div>
      )}

      {/* ── Backup & restore tab ─────────────────────────────────────────── */}
      {activeTab === "export" && (
        <div data-testid="data-tab-export">
          <ExportPanel />
        </div>
      )}

      {/* ── Maintenance tab ──────────────────────────────────────────────── */}
      {activeTab === "maintenance" && (
        <div data-testid="data-tab-maintenance">

          {/* Hard-delete soft-deleted leases */}
          <div data-testid="hard-delete-leases-section">
            <DataTabHeading>Hard-delete soft-deleted leases</DataTabHeading>
            <p style={{ fontSize: T.fsBase, color: T.mid, margin: "0 0 12px" }}>
              Permanently remove leases that have been soft-deleted (<code>deleted_at IS NOT NULL</code>).
              These rows are hidden from the UI but occupy space in the leases table.
              This action cannot be undone.
            </p>

            {softDeletedLeases && (
              <div
                data-testid="soft-deleted-leases-warning"
                role="alert"
                style={{
                  background: T.utilAmber,
                  border: `1px solid ${T.amber}`,
                  borderRadius: 6,
                  padding: "10px 14px",
                  marginBottom: 16,
                  fontSize: T.fsBase,
                  color: T.dark,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <strong style={{ color: T.amber }}>
                  ⚠ {softDeletedLeases.count} soft-deleted lease{softDeletedLeases.count !== 1 ? "s" : ""} detected
                </strong>
                <span style={{ fontFamily: "monospace", fontSize: T.fsXs, color: T.mid, wordBreak: "break-all" }}>
                  IDs: {softDeletedLeases.ids.join(", ")}
                </span>
              </div>
            )}

            {hardDeleteResult !== null && (
              <div
                data-testid="hard-delete-success"
                role="status"
                style={{
                  background: T.alertSuccessBg,
                  border: `1px solid ${T.alertSuccessBorder}`,
                  borderRadius: 6,
                  padding: "8px 12px",
                  marginBottom: 10,
                  fontSize: T.fsSm,
                  color: T.dark,
                }}
              >
                Hard-deleted {hardDeleteResult} lease{hardDeleteResult !== 1 ? "s" : ""} permanently.
              </div>
            )}

            {hardDeleteError && (
              <div
                data-testid="hard-delete-error"
                role="alert"
                style={{
                  background: T.alertErrBg,
                  border: `1px solid ${T.red}`,
                  borderRadius: 6,
                  padding: "8px 12px",
                  marginBottom: 10,
                  fontSize: T.fsSm,
                  color: T.dark,
                }}
              >
                {hardDeleteError.message}
              </div>
            )}

            <button
              type="button"
              data-testid="hard-delete-leases-btn"
              onClick={handleOpenHardDeleteModal}
              disabled={!softDeletedLeases}
              style={{
                ...BTN_DANGER,
                opacity: !softDeletedLeases ? 0.5 : 1,
                cursor: !softDeletedLeases ? "not-allowed" : "pointer",
              }}
            >
              Hard-delete
            </button>
          </div>

          <hr style={DIVIDER} />

          {/* Re-derive distributed HC split (admin only) */}
          {currentRole === "admin" && (
            <RederiveDistributedSplitCard />
          )}
        </div>
      )}

      {/* ── Hard-delete confirmation modal ────────────────────────────── */}
      <Modal
        isOpen={hardDeleteModalOpen}
        onClose={handleCloseHardDeleteModal}
        maxWidth={480}
        title="Hard-delete soft-deleted leases"
        subTitle="This permanently removes soft-deleted leases from the database. This action cannot be undone."
        ariaLabelledBy="hard-delete-modal-title"
        testId="hard-delete-modal"
        closeTestId="hard-delete-modal-close"
        disableBackdropClose={hardDeleteBusy}
      >
        <div style={{ padding: "20px 22px 8px" }}>
          {softDeletedLeases && (
            <p
              data-testid="hard-delete-modal-ids"
              style={{ fontSize: T.fsBase, color: T.dark, margin: 0 }}
            >
              <strong>{softDeletedLeases.count}</strong> lease{softDeletedLeases.count !== 1 ? "s" : ""} will be permanently deleted:{" "}
              <span style={{ fontFamily: "monospace", fontSize: T.fsXs, color: T.mid, wordBreak: "break-all" }}>
                {softDeletedLeases.ids.join(", ")}
              </span>
            </p>
          )}

          {hardDeleteError && (
            <div
              data-testid="hard-delete-modal-error"
              role="alert"
              style={{
                marginTop: 14,
                background: T.alertErrBg,
                border: `1px solid ${T.red}`,
                borderRadius: 6,
                padding: "8px 12px",
                fontSize: T.fsSm,
                color: T.dark,
              }}
            >
              {hardDeleteError.message}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "12px 22px 20px",
            borderTop: `0.5px solid ${T.border}`,
          }}
        >
          <button
            type="button"
            data-testid="hard-delete-modal-cancel"
            onClick={handleCloseHardDeleteModal}
            disabled={hardDeleteBusy}
            style={{
              ...BTN_BASE,
              border: `1.5px solid ${T.border}`,
              background: T.card,
              color: T.mid,
              opacity: hardDeleteBusy ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="hard-delete-modal-confirm"
            onClick={handleConfirmHardDelete}
            disabled={hardDeleteBusy}
            style={{
              ...BTN_BASE,
              border: "none",
              background: hardDeleteBusy ? T.light : T.red,
              color: T.white,
              cursor: hardDeleteBusy ? "not-allowed" : "pointer",
              opacity: hardDeleteBusy ? 0.7 : 1,
            }}
          >
            {hardDeleteBusy ? "Deleting…" : "Hard-delete permanently"}
          </button>
        </div>
      </Modal>

    </div>
  );
}
